import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, ShoppingCart, Ruler } from 'lucide-react';

interface RequestQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  onOrderExistingInventory?: () => void;
  onPlaceOrderFromMeasurements?: () => void;
  title?: string;
  subtitle?: string;
  showCustomOrder?: boolean; // Control whether to show Custom Order option
  showCustomOrderFromMeasurements?: boolean; // Control whether to show Custom Order From Measurements option
  showPlaceOrderFromMeasurements?: boolean; // Control whether to show Place Order From Measurements option
}

export default function RequestQuoteModal({
  isOpen,
  onClose,
  projectId,
  onOrderExistingInventory,
  onPlaceOrderFromMeasurements,
  title = 'Request Quote',
  subtitle = 'Choose how you want to request a quote',
  showCustomOrder = true, // Default to showing Custom Order option
  showCustomOrderFromMeasurements = true, // Default to showing Custom Order From Measurements option
  showPlaceOrderFromMeasurements = false // Default to NOT showing Place Order From Measurements option
}: RequestQuoteModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleOptionClick = (option: string) => {
    console.log('Selected option:', option);
    
    if (option === 'existing-inventory' && onOrderExistingInventory) {
      onOrderExistingInventory();
      onClose();
    } else if (option === 'place-order-from-measurements' && onPlaceOrderFromMeasurements) {
      onPlaceOrderFromMeasurements();
      onClose();
    } else {
      // TODO: Handle other options
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100030,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[500px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100040
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {subtitle}
            </p>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
              {/* Order Existing Inventory */}
              <button
                onClick={() => handleOptionClick('existing-inventory')}
                className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      Order Existing Inventory
                    </h3>
                    <p className="text-sm text-gray-500">
                      Request a quote for items from your existing inventory
                    </p>
                  </div>
                </div>
              </button>

              {/* Custom Order - Only show if showCustomOrder is true */}
              {showCustomOrder && (
                <button
                  onClick={() => handleOptionClick('custom-order')}
                  className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <ShoppingCart className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Custom Order
                      </h3>
                      <p className="text-sm text-gray-500">
                        Request a quote for custom items not in your inventory
                      </p>
                    </div>
                  </div>
                </button>
              )}

              {/* Custom Order From Measurements - Only show if showCustomOrderFromMeasurements is true */}
              {showCustomOrderFromMeasurements && (
                <button
                  onClick={() => handleOptionClick('custom-order-from-measurements')}
                  className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <Ruler className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Custom Order From Measurements
                      </h3>
                      <p className="text-sm text-gray-500">
                        Request a quote based on project measurements
                      </p>
                    </div>
                  </div>
                </button>
              )}

              {/* Place Order From Measurements - Only show if showPlaceOrderFromMeasurements is true */}
              {showPlaceOrderFromMeasurements && (
                <button
                  onClick={() => handleOptionClick('place-order-from-measurements')}
                  className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <Ruler className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Place Order From Measurements
                      </h3>
                      <p className="text-sm text-gray-500">
                        Create an order directly from project measurements
                      </p>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

